"use client";
import type {
  AuditFormProps,
  SessionProgress,
  SetSessionProgress,
} from "../../../types/audit-form";

import type { AuditFileMetadata } from "../../../types/audit-file";
import { useImageDescriptions } from "../../../hooks/use-image-descriptions";
import {
  errorMessage,
  requireMutationSuccess,
} from "../../../utils/mutationResult.js";

import {
  AlertCircle,
  Building,
  Camera,
  Eye,
  Loader2,
  Save,
  Trash2,
  User,
  X,
} from "lucide-react";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAudit } from "../../../contexts/audit-context";
import { useDebouncedSave } from "../../../hooks/use-debounced-save";
import { useDraftAutosave } from "../../../hooks/use-draft-autosave";
import {
  useVersionAwareFiles,
  useVersionAwareProjectData,
} from "../../../hooks/use-version-aware-data";
import { useNotification } from "../../../hooks/useNotification";
import { notificationHelpers } from "../../../utils/notificationHelpers";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button.jsx";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card.jsx";
import {
  OptimizedInput,
  OptimizedMultiSelect,
  OptimizedSelect,
  OptimizedTextarea,
} from "../optimized-form-field";

import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import {
  deleteFile,
  fetchFiles,
  updateFile,
  uploadMultipleFiles,
} from "../../../redux/slices/fileSlice.js";
import { updateSiteData } from "../../../redux/slices/siteDataSlice.js";
import { OfflineImage } from "../../shared/offline-image";
import { ImageCapturePicker } from "./image-capture-picker";
import { LocalSectionImportControl } from "./local-section-import-control";
import { sharedSiteOverviewProgress } from "../../../utils/componentUtils";

interface ProjectDataFormProps extends AuditFormProps {
  siteId: string;
  reportId: string;
  templateId: string;
  sessionProgress: SessionProgress;
  setSessionProgress: SetSessionProgress;
  readOnly?: boolean;
  previousCompletedReports?: any[];
}

interface ProjectImage extends AuditFileMetadata {
  id: string;
  name: string;
  url: string;
  type: string;
  size: number;
  description: string;
  uploadDate: Date;
}

interface ProjectData {
  // Basic Information
  name: string;
  country: string;
  postcode: string;
  town: string;

  // Contact Information
  projectManagerContact: string;
  landlordContactDetails: string;
  phone: string;
  mobile: string;
  fax: string;
  email: string;

  // System Specifications
  moduleType: string[]; // multi-select
  inverterType: string; // single-select
  mountingRack: string[]; // multi-select
  sizeOfArea: string;
  plantCapacity: string;
  exportCapacity: string;
  dcCapacity: string;
  acCapacity: string;

  // Additional Information
  projectInformationNotes: string;
  systemSpecificationNotes: string;
  additionalNotes: string;

  // Images
  projectImages: ProjectImage[];
}

interface ImageCardProps {
  image: ProjectImage;
  imageDescription: string;
  onView: (image: ProjectImage) => void;
  onRemove: (imageId: string) => void;
  onDescriptionUpdate: (imageId: string, description: string) => void;
  isDeleting: boolean;
  readOnly: boolean;
  loading: boolean;
}

const formatFileSize = (bytes: number) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return (
    Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i]
  );
};

const ImageCard = memo(
  function ImageCard({
    image,
    imageDescription,
    onView,
    onRemove,
    onDescriptionUpdate,
    isDeleting,
    readOnly,
    loading,
  }: ImageCardProps) {
    const [localDescription, setLocalDescription] = useState(imageDescription);

    useEffect(() => {
      setLocalDescription(imageDescription);
    }, [imageDescription]);

    const handleChange = useCallback(
      (value: string) => {
        setLocalDescription(value);
        onDescriptionUpdate(image.id, value);
      },
      [image.id, onDescriptionUpdate],
    );

    const handleBlur = useCallback(() => {
      onDescriptionUpdate(image.id, localDescription);
    }, [image.id, localDescription, onDescriptionUpdate]);

    return (
      <div className="relative group">
        <div className="aspect-video bg-slate-100 rounded-lg overflow-hidden relative dark:bg-muted">
          <OfflineImage
            fileId={image?.id}
            src={image?.fileUrl}
            alt="Site image"
            className="w-full h-full object-cover"
          />
          {loading && isDeleting && (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
              <Loader2 className="w-6 h-6 text-white animate-spin" />
            </div>
          )}
        </div>
        <div className="absolute top-2 right-2 flex gap-1">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity bg-black/60 hover:bg-black/80 text-white rounded-full flex items-center justify-center"
            onClick={() => onView(image)}
            disabled={loading}
            title="View Image"
          >
            <Eye className="w-3 h-3" />
          </Button>
          {!readOnly && (
            <Button
              type="button"
              variant="destructive"
              size="sm"
              className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity bg-black/60 hover:bg-black/80 text-white rounded-full flex items-center justify-center"
              onClick={() => onRemove(image.id)}
              disabled={loading}
              title="Delete Image"
            >
              <Trash2 className="w-3 h-3" />
            </Button>
          )}
        </div>
        <div className="mt-2 space-y-1">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-slate truncate">
              {image?.originalName}
            </span>
            <Badge variant="outline" className="text-xs">
              {formatFileSize(image?.fileSize)}
            </Badge>
          </div>
          <OptimizedTextarea
            id={`desc-${image.id}`}
            label=""
            value={localDescription}
            readOnly={readOnly}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Describe this image..."
            rows={2}
            disabled={loading}
          />
          <div className="text-xs text-slate-500 dark:text-muted-foreground">
            Date: {new Date(image.uploadDate).toLocaleDateString()}
          </div>
        </div>
      </div>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.image.id === nextProps.image.id &&
      prevProps.imageDescription === nextProps.imageDescription &&
      prevProps.isDeleting === nextProps.isDeleting &&
      prevProps.readOnly === nextProps.readOnly &&
      prevProps.loading === nextProps.loading
    );
  },
);

const initialData: ProjectData = {
  // Basic Information
  name: "",
  country: "",
  postcode: "",
  town: "",

  // Contact Information
  projectManagerContact: "",
  landlordContactDetails: "",
  phone: "",
  mobile: "",
  fax: "",
  email: "",

  // System Specifications
  moduleType: [],
  inverterType: "",
  mountingRack: [],
  sizeOfArea: "",
  plantCapacity: "",
  exportCapacity: "",
  dcCapacity: "",
  acCapacity: "",

  // Additional Information
  projectInformationNotes: "",
  systemSpecificationNotes: "",
  additionalNotes: "",

  // Images
  projectImages: [],
};

// Options for multi-select and single-select fields
const countryOptions = [
  { value: "UK", label: "United Kingdom" },
  { value: "IE", label: "Ireland" },
  { value: "FR", label: "France" },
  { value: "DE", label: "Germany" },
  { value: "ES", label: "Spain" },
  { value: "IT", label: "Italy" },
  { value: "NL", label: "Netherlands" },
  { value: "BE", label: "Belgium" },
  { value: "US", label: "United States" },
  { value: "CA", label: "Canada" },
  { value: "AU", label: "Australia" },
  { value: "IN", label: "India" },
  { value: "CN", label: "China" },
  { value: "JP", label: "Japan" },
];

const moduleTypeOptions = [
  { value: "thin_film", label: "Thin Film" },
  { value: "mono_crystalline", label: "Mono Crystalline" },
  { value: "poly_crystalline", label: "Poly Crystalline" },
  { value: "perc", label: "PERC" },
  { value: "topcon", label: "TopCon" },
  { value: "half_cell", label: "Half-Cell" },
  { value: "bifacial", label: "Bifacial" },
];

const inverterTypeOptions = [
  { value: "central", label: "Central" },
  { value: "string", label: "String" },
];

const mountingRackOptions = [
  // Ground-mount structures
  { value: "single_post", label: "Single Post" },
  { value: "double_post", label: "Double Post" },
  { value: "ballast_system", label: "Ballast System" },
  { value: "landscape", label: "Landscape" },
  { value: "portrait", label: "Portrait" },
  { value: "single_axis_tracker", label: "Single Axis Tracker" },
  { value: "double_axis_tracker", label: "Double Axis Tracker" },
  { value: "rail_mounted", label: "Rail Mounted" },
  { value: "flush_mount", label: "Flush Mount" },
  { value: "tilt_mount", label: "Tilt Mount" },
  // Rooftop-mount structures
  {
    value: "roof_clamped_standing_seam",
    label: "Roof Clamped to Standing Seam",
  },
  {
    value: "roof_clamped_trapezoidal_sheet",
    label: "Roof Clamped to Trapezoidal Metal Sheet",
  },
  { value: "direct_fix_purlins", label: "Direct Fix to Purlins" },
  { value: "direct_fix_rafters", label: "Direct Fix to Rafters" },
  {
    value: "raised_framed_roof_structure",
    label: "Raised Framed Roof Structure",
  },
  {
    value: "ground_mount_framework_on_roof",
    label: "Ground-Mount Type Framework on Roof",
  },
  { value: "ballasted_roof_system", label: "Ballasted Roof System" },
];


export const ProjectDataForm = memo(function ProjectDataForm({
  siteId,
  reportId,
  templateId,
  sessionProgress,
  setSessionProgress,
  readOnly = false,
  previousCompletedReports = [],
}: ProjectDataFormProps) {
  const { loading } = useAppSelector((state) => state?.files);
  const { notify } = useNotification();
  const [formData, setFormData] = useState<ProjectData>(initialData);
  useAudit();
  const [selectedImage, setSelectedImage] = useState<ProjectImage | null>(null);
  const [imageToDelete, setImageToDelete] = useState<string | null>(null);
  const [uploadingImages, setUploadingImages] = useState<{
    [key: string]: boolean;
  }>({});

  const imageDescriptionBaselineRef = useRef<Record<string, string>>({});
  const currentFilesData = useVersionAwareFiles(readOnly);
  const dispatch = useAppDispatch();
  const currentProjectData = useVersionAwareProjectData(readOnly);
  const files = useVersionAwareFiles(readOnly);
  const [imageDescriptions, setImageDescriptions] = useImageDescriptions(files);

  // Phase 2: autosave unsaved edits to IndexedDB so nothing is lost if the
  // app closes before the debounced server save fires (e.g. offline, tab
  // closed, phone loses power).
  const draftKey =
    !readOnly && reportId ? `report:${reportId}:site-overview` : null;
  const { restoreDraft, clearSavedDraft } = useDraftAutosave(
    draftKey,
    formData,
  );
  const draftRestoredRef = useRef(false);

  useEffect(() => {
    dispatch(
      fetchFiles({
        siteId,
        reportId,
      }),
    );
  }, [siteId, reportId, dispatch]);

  // Restore any unsaved draft once on mount, before server data can
  // overwrite it below. Only applies if the draft is newer than what's
  // already loaded, so a stale draft never clobbers a freshly-fetched
  // server record from a different session.
  useEffect(() => {
    if (!draftKey || draftRestoredRef.current) return;
    draftRestoredRef.current = true;
    restoreDraft().then((draft) => {
      if (draft?.data) {
        setFormData((prev: any) => ({ ...prev, ...draft.data }));
      }
    });
  }, [draftKey, restoreDraft]);

  // Once the local form state matches what's confirmed saved on the
  // server, the IndexedDB draft is redundant — clear it so a future mount
  // doesn't restore stale local data over a fresher server record (e.g.
  // after editing the same report from a different device).
  useEffect(() => {
    if (!draftKey || !currentProjectData) return;
    if (JSON.stringify(currentProjectData) === JSON.stringify(formData)) {
      clearSavedDraft();
    }
  }, [draftKey, currentProjectData, formData, clearSavedDraft]);

  const documents = useMemo(() => {
    if (currentFilesData?.length > 0) {
      return {
        projectImages:
          currentFilesData?.filter(
            (file: any) =>
              file?.relatedTo?.entityType === "project" &&
              file?.relatedTo?.entityId === reportId &&
              file?.relatedTo?.filterData?.project_images,
          ) || [],
      };
    } else {
      return { projectImages: [] };
    }
  }, [currentFilesData, reportId]);

  const handleSave = useCallback(
    async (data: ProjectData) => {
      try {
        if (!siteId || !reportId) {
          throw new Error("Site or report context is missing");
        }

        // await dispatch(
        //   updateSiteOverviewData({
        //     siteId,
        //     reportId,
        //     payload: {
        //       projectData: data,
        //     },
        //   }),
        // );
        const mutationResult1 = await requireMutationSuccess(
          dispatch(
            updateSiteData({
              siteDataId: siteId,
              siteDataInfo: { projectData: data },
            }),
          ),
        );

        const config = notificationHelpers.formSaveSuccess("Site Data", [
          mutationResult1,
        ]);
        notify(config.message, config.type, config.options);
      } catch (error) {
        const config = notificationHelpers.formSaveError(
          "Site Data",
          errorMessage(error),
        );
        notify(config.message, config.type, config.options);

        throw error;
      }
    },
    [siteId, reportId, dispatch, notify],
  );

  const {
    isSaving: isFormSaving,
    hasUnsavedChanges: hasFormUnsavedChanges,
    forceSave: forceFormSave,
    resetBaseline: resetFormBaseline,
  } = useDebouncedSave(formData, {
    delay: 7500,
    onSave: handleSave,
    onError: (error) => console.error("Save error:", error),
    readOnly: readOnly,
  });

  useEffect(() => {
    if (!currentProjectData) return;

    const newFormData = {
      ...initialData,
      name: currentProjectData?.name || "",
      country: currentProjectData?.country || "",
      postcode: currentProjectData?.postcode || "",
      town: currentProjectData?.town || "",
      projectManagerContact: currentProjectData?.projectManagerContact || "",
      landlordContactDetails: currentProjectData?.landlordContactDetails || "",
      phone: currentProjectData?.phone || "",
      mobile: currentProjectData?.mobile || "",
      fax: currentProjectData?.fax || "",
      email: currentProjectData?.email || "",
      moduleType: currentProjectData?.moduleType || [],
      inverterType: currentProjectData?.inverterType || "",
      mountingRack: currentProjectData?.mountingRack || [],
      sizeOfArea: currentProjectData?.sizeOfArea || "",
      plantCapacity: currentProjectData?.plantCapacity || "",
      exportCapacity: currentProjectData?.exportCapacity || "",
      dcCapacity: currentProjectData?.dcCapacity || "",
      acCapacity: currentProjectData?.acCapacity || "",
      projectInformationNotes:
        currentProjectData?.projectInformationNotes || "",
      systemSpecificationNotes:
        currentProjectData?.systemSpecificationNotes || "",
      additionalNotes: currentProjectData?.additionalNotes || "",
    };

    resetFormBaseline(newFormData);
    setFormData(newFormData);
  }, [currentProjectData, resetFormBaseline, setSessionProgress, templateId]);
  useEffect(() => {

    const prog = sharedSiteOverviewProgress(
      formData,
      documents?.projectImages || []
    );
    setSessionProgress((prev) => {
      if (prev?.[templateId]?.["site-overview"] === prog) return prev;

      return {
        ...prev,
        [templateId]: { ...prev[templateId], "site-overview": prog },
      };
    });
  }, [
    formData,
    templateId,
  documents?.projectImages,
    setSessionProgress,
  ]);
  useEffect(() => {
    imageDescriptionBaselineRef.current = Object.fromEntries(
      (files || []).map((file) => [file.id, file?.fileDescription ?? ""]),
    );
  }, [files]);

  // Save handler for image descriptions
  const handleSaveImages = useCallback(
    async (descriptions: Record<string, string>) => {
      try {
        const changedDescriptions = Object.entries(descriptions).filter(
          ([fileId, fileDescription]) =>
            imageDescriptionBaselineRef.current[fileId] !== fileDescription,
        );

        if (changedDescriptions.length === 0) return;

        const mutationResult2 = await Promise.all(
          changedDescriptions.map(([fileId, fileDescription]) =>
            requireMutationSuccess(
              dispatch(updateFile({ fileId, fileData: { fileDescription } })),
            ),
          ),
        );

        changedDescriptions.forEach(([fileId, fileDescription]) => {
          imageDescriptionBaselineRef.current[fileId] = fileDescription;
        });

        const config = notificationHelpers.formSaveSuccess(
          "Image Descriptions",
          [mutationResult2],
        );
        notify(config.message, config.type, config.options);
      } catch (error) {
        const config = notificationHelpers.formSaveError(
          "Image Descriptions",
          errorMessage(error),
        );
        notify(config.message, config.type, config.options);

        throw error;
      }
    },
    [dispatch, notify],
  );

  const {
    isSaving: isImageSaving,
    hasUnsavedChanges: hasImageUnsavedChanges,
    forceSave: forceImageSave,
  } = useDebouncedSave(imageDescriptions, {
    delay: 5000,
    onSave: handleSaveImages,
    onError: (error) => console.error("Image description save error:", error),
    readOnly,
  });

  const isSaving = isFormSaving || isImageSaving;
  const hasUnsavedChanges = hasFormUnsavedChanges || hasImageUnsavedChanges;

  const forceSave = useCallback(() => {
    if (hasFormUnsavedChanges) forceFormSave();
    if (hasImageUnsavedChanges) forceImageSave();
  }, [
    forceFormSave,
    forceImageSave,
    hasFormUnsavedChanges,
    hasImageUnsavedChanges,
  ]);

  const isFormValid = useMemo(() => {
    return (
      formData?.name?.trim() !== "" &&
      formData?.country?.trim() !== "" &&
      formData?.postcode?.trim() !== "" &&
      formData?.town?.trim() !== "" &&
      formData?.projectManagerContact?.trim() !== "" &&
      formData?.landlordContactDetails?.trim() !== "" &&
      formData?.phone?.trim() !== "" &&
      formData?.mobile?.trim() !== "" &&
      formData?.fax?.trim() !== "" &&
      formData?.email?.trim() !== "" &&
      formData?.moduleType?.length > 0 &&
      formData?.inverterType?.trim() !== "" &&
      formData?.mountingRack?.length > 0 &&
      formData?.sizeOfArea?.trim() !== "" &&
      // formData?.plantCapacity?.trim() !== "" &&
      formData?.exportCapacity?.trim() !== "" &&
      formData?.dcCapacity?.trim() !== "" &&
      formData?.acCapacity?.trim() !== "" &&
      formData?.projectInformationNotes?.trim() !== "" &&
      formData?.systemSpecificationNotes?.trim() !== "" &&
      formData?.additionalNotes?.trim() !== ""
    );
  }, [formData]);

  const handleFieldChange = useCallback((field: keyof ProjectData) => {
    return (value: string | string[]) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
    };
  }, []);

  const handleMultiSelectChange = useCallback((field: keyof ProjectData) => {
    return (values: string[]) => {
      setFormData((prev) => ({ ...prev, [field]: values }));
    };
  }, []);

  const handleImageUpload = useCallback(
    (files: FileList | File[] | null, filterData) => {
      if (!files) return;
      const fileArray = Array.from(files);

      // Set uploading state for this specific upload
      const uploadKey = JSON.stringify(filterData);
      setUploadingImages((prev) => ({ ...prev, [uploadKey]: true }));

      requireMutationSuccess(
        dispatch(
          uploadMultipleFiles({
            files: fileArray,
            metadata: {
              siteId: siteId,
              entityType: "project",
              entityId: reportId,
              reportId,
            },
            filterData,
          }),
        ),
      )
        .then((mutationOutcome) => {
          // Show success notification for each uploaded file
          fileArray.forEach((file) => {
            const config = notificationHelpers.imageUploadSuccess(
              file.name,
              mutationOutcome,
            );
            notify(config.message, config.type, config.options);
          });
        })
        .catch((error) => {
          // Show error notification
          const config = notificationHelpers.imageUploadError(
            fileArray.length === 1
              ? fileArray[0].name
              : `${fileArray.length} files`,
            errorMessage(error),
          );
          notify(config.message, config.type, config.options);
        })
        .finally(() => {
          // Clear uploading state when done
          setUploadingImages((prev) => ({ ...prev, [uploadKey]: false }));
        });
    },
    [dispatch, siteId, reportId, notify],
  );

  const updateImageDescription = useCallback(
    (imageId: string, description: string) => {
      setImageDescriptions((prev) => ({ ...prev, [imageId]: description }));
    },
    [setImageDescriptions],
  );

  const handleRemoveImage = useCallback((imageId: string) => {
    setImageToDelete(imageId);
  }, []);

  const confirmRemoveImage = useCallback(() => {
    if (readOnly) {
      setImageToDelete(null);
      const config = notificationHelpers.readOnlyMode();
      notify(config.message, config.type, config.options);
      return;
    }
    if (imageToDelete) {
      requireMutationSuccess(dispatch(deleteFile(imageToDelete)))
        .then((mutationOutcome) => {
          const config = notificationHelpers.imageDeleteSuccess(
            "",
            mutationOutcome,
          );
          notify(config.message, config.type, config.options);
        })
        .catch((error) => {
          const config = notificationHelpers.imageDeleteError(
            "",
            errorMessage(error),
          );
          notify(config.message, config.type, config.options);
        })
        .finally(() => {
          setImageToDelete(null);
        });
    }
  }, [imageToDelete, readOnly, dispatch, notify]);

  const cancelRemoveImage = useCallback(() => {
    setImageToDelete(null);
  }, []);

  // Helper function to check if any upload is in progress for a specific section
  const isUploadingForSection = (filterData: any) => {
    const uploadKey = JSON.stringify(filterData);
    return uploadingImages[uploadKey] || false;
  };

  return (
    <div className="space-y-4 md:space-y-6 relative ">
      {!readOnly && (
        <LocalSectionImportControl
          previousCompletedReports={previousCompletedReports}
          sectionName="site-overview"
          siteId={siteId}
          reportId={reportId}
          readOnly={readOnly}
          setFormData={setFormData}
        />
      )}

      {/* Floating save section with tooltip - hidden when viewing read-only version */}
      {!readOnly && (
        <div className="fixed top-25 right-3 z-40 sm:top-20 sm:right-4">
          <div className="flex flex-row items-center gap-2 sm:flex-col sm:items-end sm:gap-3">
            {/* Status indicator - same size as save button */}
            {/* <div
        className="
          bg-white/95 backdrop-blur-sm
          rounded-full border-2 border-gray-200/80
          shadow-lg
          w-12 h-12 sm:w-16 sm:h-16
          flex items-center justify-center
          group relative
          transition-all duration-300 ease-out
          transform hover:scale-110
          cursor-default
          p-1 sm:p-2
        "
      >
        <div className="flex flex-col items-center justify-center w-full h-full">
          <span className="hidden text-[10px] font-medium text-gray-600 leading-none mb-2 sm:block">
            Saved
          </span>

          <div className="flex items-center justify-center h-6">
            <LoadingIndicator
              status={getLoadingStatus()}
              lastSaved={lastSaved}
              error={saveError}
            />
          </div>
        </div>

        <div className="absolute right-full mr-2 px-2 py-1 bg-black text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
          {getLoadingStatus() === "saving"
            ? "Saving..."
            : getLoadingStatus() === "saved"
              ? `Saved ${lastSaved?.toLocaleTimeString()}`
              : getLoadingStatus() === "error"
                ? "Save failed"
                : "Ready to save"}
        </div>

        {getLoadingStatus() === "error" && (
          <div className="absolute -top-1 -right-1">
            <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
            <div className="w-3 h-3 bg-red-500 rounded-full absolute top-0 left-0 border-2 border-white"></div>
          </div>
        )}
      </div> */}

            {/* Save button - Fixed with pointer-events-none */}
            <Button
              onClick={forceSave}
              disabled={isSaving || readOnly}
              className={`
          relative rounded-full w-12 h-12 sm:w-16 sm:h-16
          transition-all duration-300 ease-out
          flex flex-col items-center justify-center
          backdrop-blur-sm border-2
          shadow-lg hover:shadow-xl
          transform hover:scale-110 active:scale-95
          group
          ${
            hasUnsavedChanges && !readOnly
              ? "bg-gradient-to-br from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white border-red-300 dark:bg-gradient-to-br dark:border-red-300 dark:ring-red-300"
              : "bg-white/95 text-gray-600 border-gray-200/80 hover:bg-white/90 dark:bg-muted dark:text-muted-foreground dark:border-border dark:hover:bg-accent"
          }
          ${isSaving || readOnly ? "opacity-80 cursor-not-allowed" : "cursor-pointer"}
        `}
            >
              {isSaving ? (
                <div className="flex flex-col items-center justify-center">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mb-2"></div>
                  <span className="text-[10px] leading-none">Saving</span>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center">
                  <Save className="w-5 h-5 sm:mb-1" />
                  <span className="hidden text-[10px] font-medium leading-none sm:block">
                    Save
                  </span>
                </div>
              )}

              {/* Unsaved changes indicator */}
              {hasUnsavedChanges && !isSaving && (
                <div className="absolute -top-1 -right-1">
                  <div className="w-3 h-3 bg-red-500 rounded-full animate-ping"></div>
                  <div className="w-3 h-3 bg-red-500 rounded-full absolute top-0 left-0 border-2 border-white"></div>
                </div>
              )}

              {/* Tooltip - Fixed with pointer-events-none */}
              <div className="absolute right-full mr-2 px-2 py-1 bg-black text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                {hasUnsavedChanges ? "Save changes" : "No changes to save"}
              </div>
            </Button>
          </div>
        </div>
      )}

      {/* Basic Information Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <Building className="w-5 h-5 text-blue-600" />
            <CardTitle>Basic Information</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <OptimizedInput
              id="name"
              label="Site Name"
              value={formData.name}
              onChange={handleFieldChange("name")}
              placeholder="Enter site name"
              readOnly={readOnly}
              required
            />
            <OptimizedSelect
              id="country"
              label="Country"
              value={formData.country}
              onChange={handleFieldChange("country")}
              placeholder="Select country"
              options={countryOptions}
              required

              readOnly={readOnly}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <OptimizedInput
              id="postcode"
              label="Postcode"
              value={formData.postcode}
              onChange={handleFieldChange("postcode")}
              placeholder="Enter postcode"
              required

              readOnly={readOnly}
            />
            <OptimizedInput
              id="town"
              label="Town/City"
              value={formData.town}
              onChange={handleFieldChange("town")}
              placeholder="Enter town or city"
              required
              readOnly={readOnly}
            />
          </div>

          {/* <OptimizedTextarea
            id="projectInformationNotes"
            label="Site Information Notes"
            value={formData.projectInformationNotes}
            onChange={handleFieldChange("projectInformationNotes")}
            placeholder="Enter any additional site information notes or special requirements..."
            rows={3}
            readOnly={readOnly}
          /> */}
        </CardContent>
      </Card>

      {/* Contact Information Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <User className="w-5 h-5 text-green-600" />
            <CardTitle>Contact Information</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <OptimizedInput
              id="projectManagerContact"
              label="Site Manager Contact"
              value={formData.projectManagerContact}
              onChange={handleFieldChange("projectManagerContact")}
              placeholder="Enter site manager contact details"

              readOnly={readOnly}
            />
            <OptimizedInput
              id="landlordContactDetails"
              label="Landlord Contact Details"
              value={formData.landlordContactDetails}
              onChange={handleFieldChange("landlordContactDetails")}
              placeholder="Enter landlord contact details"
              readOnly={readOnly}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <OptimizedInput
              id="phone"
              label="Phone"
              value={formData.phone}
              onChange={handleFieldChange("phone")}
              placeholder="Enter phone number"
              required

              readOnly={readOnly}
            />
            <OptimizedInput
              id="email"
              label="Email"
              value={formData.email}
              onChange={handleFieldChange("email")}
              placeholder="Enter email address"
              type="email"
              required

              readOnly={readOnly}
            />
            {/* <OptimizedInput
              id="mobile"
              label="Mobile"
              value={formData.mobile}
              onChange={handleFieldChange("mobile")}
              placeholder="Enter mobile number"
              required
              readOnly={readOnly}
            /> */}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <OptimizedInput
              id="fax"
              label="Fax"
              value={formData.fax}
              onChange={handleFieldChange("fax")}
              placeholder="Enter fax number"
              readOnly={readOnly}
            />
          </div>
        </CardContent>
      </Card>

      {/* System Specifications Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <svg
              className="w-5 h-5 text-purple-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z"
              />
            </svg>
            <CardTitle>System Specifications</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <OptimizedMultiSelect
              id="moduleType"
              label="Module Type"
              values={formData.moduleType}
              onChange={handleMultiSelectChange("moduleType")}
              placeholder="Select module types"
              options={moduleTypeOptions}
              required
              readOnly={readOnly}
              showValues={true}
            />
            <OptimizedSelect
              id="inverterType"
              label="Inverter Type"
              value={formData.inverterType}
              onChange={handleFieldChange("inverterType")}
              placeholder="Select inverter type"
              options={inverterTypeOptions}
              required
              readOnly={readOnly}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <OptimizedMultiSelect
              id="mountingRack"
              label="Mounting Rack"
              values={formData.mountingRack}
              onChange={handleMultiSelectChange("mountingRack")}
              placeholder="Select mounting rack types"
              options={mountingRackOptions}
              required
              readOnly={readOnly}
              showValues={true}
            />
            <OptimizedInput
              id="sizeOfArea"
              label="Size of Area (m² / ha)"
              value={formData.sizeOfArea}
              onChange={handleFieldChange("sizeOfArea")}
              placeholder="e.g., 5000 m² or 0.5 ha"
              type="number"
              step="0.01"
              readOnly={readOnly}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <OptimizedInput
              id="dcCapacity"
              label="DC Capacity (kWP)"
              value={formData.dcCapacity}
              onChange={handleFieldChange("dcCapacity")}
              placeholder="e.g., 1100"
              type="number"
              step="0.1"
              required
              readOnly={readOnly}
            />
            <OptimizedInput
              id="acCapacity"
              label="AC Capacity (kW)"
              value={formData.acCapacity}
              onChange={handleFieldChange("acCapacity")}
              placeholder="e.g., 1000"
              type="number"
              step="0.1"
              required
              readOnly={readOnly}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* <OptimizedInput
              id="plantCapacity"
              label="Plant Capacity (kWp)"
              value={formData.plantCapacity}
              onChange={handleFieldChange("plantCapacity")}
              placeholder="e.g., 1000"
              type="number"
              step="0.1"
              required
              readOnly={readOnly}
            /> */}
            <OptimizedInput
              id="exportCapacity"
              label="Export Capacity (kW)"
              value={formData.exportCapacity}
              onChange={handleFieldChange("exportCapacity")}
              placeholder="e.g., 1000"
              type="number"
              step="100"
              required
              readOnly={readOnly}
            />

            <OptimizedInput
              id="acDCRatio"
              label="DC:AC Ratio (it will automatically calculate based on DC and AC capacity)"
              value={
                formData.dcCapacity && formData.acCapacity
                  ? Number(
                      (
                        Number(formData.dcCapacity) /
                        Number(formData.acCapacity)
                      ).toFixed(2),
                    ).toString()
                  : "0"
              }
              placeholder="e.g., 1000"
              type="number"
              readOnly
              required
            />
          </div>

          <OptimizedTextarea
            id="systemSpecificationNotes"
            label="System Specification Notes"
            value={formData.systemSpecificationNotes}
            onChange={handleFieldChange("systemSpecificationNotes")}
            placeholder="Enter any additional system specifications or special requirements..."
            rows={3}
            readOnly={readOnly}
          />
        </CardContent>
      </Card>

      {/* Project Images Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Camera className="w-5 h-5" />
              <CardTitle>Site Overview Images</CardTitle>
              <span className="text-red-500">*</span>
            </div>

            {!readOnly && (
              <ImageCapturePicker
                buttonText="Add Images"
                isUploading={isUploadingForSection({ project_images: true })}
                disabled={loading || readOnly}
                onConfirm={(selectedFiles) =>
                  handleImageUpload(selectedFiles, { project_images: true })
                }
              />
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {isUploadingForSection({ project_images: true }) && (
            <div className="flex items-center justify-center py-6 border border-dashed border-slate-300 rounded-lg bg-slate-50 dark:bg-muted dark:border-border">
              <div className="text-center">
                <Loader2 className="w-6 h-6 text-slate-400 animate-spin mx-auto mb-2 dark:text-muted-foreground" />
                <p className="text-sm text-slate-600 dark:text-muted-foreground">
                  Uploading site images...
                </p>
              </div>
            </div>
          )}

          {!isUploadingForSection({ project_images: true }) &&
          documents.projectImages?.length === 0 ? (
            <div className="flex items-center justify-between p-4 border border-dashed border-slate-300 rounded-lg bg-slate-50 dark:bg-muted dark:border-border">
              <div className="flex items-center gap-3">
                <Camera className="w-5 h-5 text-slate-400 dark:text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium text-slate-700 dark:text-foreground">
                    No site images yet
                  </p>
                  <p className="text-xs text-slate-500 dark:text-muted-foreground">
                    Add images to document the site setup and progress
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {documents.projectImages?.map((image) => (
                <ImageCard
                  key={image.id}
                  image={image}
                  imageDescription={
                    imageDescriptions[image.id] ?? image?.fileDescription ?? ""
                  }
                  onView={setSelectedImage}
                  onRemove={handleRemoveImage}
                  onDescriptionUpdate={updateImageDescription}
                  isDeleting={imageToDelete === image.id}
                  readOnly={readOnly}
                  loading={loading}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Additional Notes Card */}
      <Card>
        <CardHeader>
          <CardTitle>Additional Notes</CardTitle>
        </CardHeader>
        <CardContent>
          <OptimizedTextarea
            id="additionalNotes"
            label="Site Notes"
            value={formData.additionalNotes}
            onChange={handleFieldChange("additionalNotes")}
            placeholder="Enter any additional site information or special requirements..."
            rows={4}
            readOnly={readOnly}
          />
        </CardContent>
      </Card>

      {/* {!isFormValid && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Please fill in all required fields (marked with *) to complete this
            section.
          </AlertDescription>
        </Alert>
      )} */}

      {/* Delete Confirmation Modal */}
      {imageToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-card rounded-lg max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <AlertCircle className="w-6 h-6 text-red-500" />
              <h3 className="text-lg font-semibold">Delete Image</h3>
            </div>
            <p className="text-slate-600 dark:text-muted-foreground mb-6">
              Are you sure you want to delete this image? This action cannot be
              undone.
            </p>
            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={cancelRemoveImage}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={confirmRemoveImage}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Image Preview Modal */}
      {selectedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-card rounded-lg max-w-4xl max-h-full overflow-auto">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold">
                {selectedImage.originalName}
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedImage(null)}
                disabled={loading}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="p-4">
              <OfflineImage
                fileId={selectedImage.id}
                src={selectedImage?.fileUrl}
                alt={selectedImage.originalName}
                className="max-w-full max-h-96 mx-auto"
              />
              {selectedImage.fileDescription && (
                <div className="mt-4 p-3 bg-slate-50 dark:bg-muted rounded">
                  <p className="text-sm text-slate-700 dark:text-muted-foreground">
                    {selectedImage.fileDescription}
                  </p>
                </div>
              )}
              <div className="mt-2 text-sm text-slate-500 dark:text-muted-foreground">
                Size: {formatFileSize(selectedImage.fileSize)} • Date:{" "}
                {new Date(selectedImage.uploadDate).toLocaleDateString()}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bottom-2 md:bottom-4 z-20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-3 md:p-4 bg-white dark:bg-card rounded-lg border shadow-lg backdrop-blur-sm bg-white/95 dark:bg-surface-raised/95">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 w-full sm:w-auto">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-slate-700 dark:text-muted-foreground">
              Form Status:
            </span>
            <Badge variant={isFormValid ? "default" : "secondary"}>
              {isFormValid ? "Complete" : "Incomplete"}
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-slate-700 dark:text-muted-foreground">
              Progress:
            </span>
            <Badge variant="outline">
              {sessionProgress[templateId]?.["site-overview"]}%
            </Badge>
          </div>
        </div>
        <div className="text-xs md:text-sm text-slate-500 dark:text-muted-foreground hidden lg:block">
          Auto-save enabled • Changes saved automatically
        </div>
      </div>
    </div>
  );
});
